import { CarrierDesk } from 'lib/hosts/quoting/carrierdesk';
import { CarrierPoint } from 'lib/hosts/quoting/carrierpoint';
import { E2Open } from 'lib/hosts/quoting/e2open';
import {
  EmergeMarket,
  isEmergeMarketHost,
} from 'lib/hosts/quoting/emergemarket';
import { FreightView } from 'lib/hosts/quoting/freightview';
import { InsightTMS, isInsightTMSUrl } from 'lib/hosts/quoting/insight-tms';
import { Kewill, isKewillUrl } from 'lib/hosts/quoting/kewill';
import {
  Manhattan,
  isManhattanOpenBroadcastUrl,
} from 'lib/hosts/quoting/manhattan';
import { NavisphereCarrier } from 'lib/hosts/quoting/navisphere-carrier';
import { OTM } from 'lib/hosts/quoting/otm';
import {
  PrincetonTmx,
  isPrincetonTmxUrl,
} from 'lib/hosts/quoting/princeton-tmx';
import { ShipperGuide } from 'lib/hosts/quoting/shipperguide';
import { Shipwell } from 'lib/hosts/quoting/shipwell';
import { Transporeon, isTransporeonHost } from 'lib/hosts/quoting/transporeon';
import {
  TruckerTools,
  isTruckerToolsHost,
  isTruckerToolsQuotePath,
} from 'lib/hosts/quoting/truckertools';
import { UberFreight } from 'lib/hosts/quoting/uberfreight';
import { Maybe, Undef } from 'types/UtilityTypes';
import captureException from 'utils/captureException';

export interface QuotingPortal {
  /**
   * Whether page snapshots should include child frames.
   */
  allFrames?: boolean;
  /**
   * The message in chrome.runtime.sendMessage() to submit a quote to this portal.
   */
  submitAction: string;
  /**
   * Determines if a quote can be submitted on the current page (used to show/hide submit buttons and to guard submit on click).
   * This function answers: "is the bid form open, or can it be opened via a click on the current webpage?"
   * @param tab The current Chrome tab
   * @param html The current document HTML
   * @returns boolean indicating if a quote can be submitted
   */
  canSubmit(tab: Undef<chrome.tabs.Tab>, html: string): boolean;
  /**
   * Optional additional info to display in the UI for this portal (e.g. hint when on a non-submission page).
   */
  additionalInfo?(tab: Undef<chrome.tabs.Tab>, html: string): Maybe<string>;
}

/**
 * Creates a new QuotingPortal instance based on the URL.
 * @param url The URL to determine which portal to create
 * @returns A new QuotingPortal instance or undefined if no matching portal is found
 */
export function newQuotingPortal(url: Undef<string>): Undef<QuotingPortal> {
  if (!url) return undefined;

  try {
    const parsedUrl = new URL(url);

    if (parsedUrl.origin?.includes('na-app.tms.e2open.com')) {
      return new E2Open();
    }

    if (parsedUrl.origin?.includes('carrier.freightview')) {
      return new FreightView();
    }

    if (isInsightTMSUrl(url)) {
      return new InsightTMS();
    }

    if (isPrincetonTmxUrl(url)) {
      return new PrincetonTmx();
    }

    if (parsedUrl.origin?.includes('app.shipwell.com')) {
      return new Shipwell();
    }

    if (parsedUrl.origin?.includes('navispherecarrier.com')) {
      return new NavisphereCarrier();
    }

    if (
      parsedUrl.hostname?.includes('uber.com') &&
      parsedUrl.pathname?.includes('/freight/carriers/fleet/')
    ) {
      return new UberFreight();
    }

    if (
      parsedUrl.hostname?.includes('carrierpoint.com') &&
      parsedUrl.pathname
        ?.toLowerCase()
        .includes('/market/jsp/cpmarketbiddingwindow.jsp')
    ) {
      return new CarrierPoint();
    }

    if (
      parsedUrl.hostname?.includes('otmgtm') &&
      parsedUrl.hostname?.includes('.oraclecloud.com')
    ) {
      return new OTM();
    }

    if (
      parsedUrl.hostname === 'carrierdesk.com' ||
      parsedUrl.hostname?.endsWith('.carrierdesk.com')
    ) {
      return new CarrierDesk();
    }

    if (
      parsedUrl.hostname === 'loadsmart.com' ||
      parsedUrl.hostname?.endsWith('.loadsmart.com')
    ) {
      return new ShipperGuide();
    }

    if (isTransporeonHost(url)) {
      return new Transporeon();
    }

    if (isEmergeMarketHost(parsedUrl.hostname)) {
      return new EmergeMarket();
    }

    if (
      isTruckerToolsHost(parsedUrl.hostname) &&
      isTruckerToolsQuotePath(parsedUrl.pathname)
    ) {
      return new TruckerTools();
    }

    if (isManhattanOpenBroadcastUrl(url)) {
      return new Manhattan();
    }

    if (isKewillUrl(url)) {
      return new Kewill();
    }

    return undefined;
  } catch (error: any) {
    captureException(error, { url });
    return undefined;
  }
}
